import React, { useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Buffer } from 'buffer';
import { LoadingButton } from '@mui/lab';
import { getValue } from 'firebase/remote-config';
import { useSnackbar } from 'notistack';
import { CLASS_OPERATIONS } from '../../../../redux/constants';
import { updateClass, deleteClassById } from '../../../../service/class.service';
import ClassInfoCard from '../../../../components/ClassInfoCard';
import palette from '../../../../theme/palette';
import '../../../../css/overview.css';
import CopyToClipBoardIcon from '../../../../svg/CopyToClipboardIcon';
import { remoteConfig } from '../../../../firebase/firebase';
import { CheckClassIsOnline } from '../../../../helper/commonHelper';
import { capturePHEvents } from '../../../../analytics/posthog.analytics';
import { PH_JOIN_CLASS } from '../../../../utils/posthog.eventNames';
import { createAttendance } from '../../../../service/attendance.service';
import { getCke } from '../../../../helper/cookieHelper';

const useStyles = makeStyles({
  containerStyle: {
    marginTop: 15
  },
  characterContainer: {
    textAlign: 'end',
    color: palette.grey[500]
  }
});

export default function TeacherOverview({ classDetails, handleClassUpdate }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [notice, setNotice] = useState(classDetails?.description || '');
  const [editNotice, setEditNotice] = useState(false);
  const [toolTip, setToolTip] = useState(false);
  const [sendBtnLoading, setSendBtnLoading] = useState(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const coupenId = useRef();
  const [isDeleteLoading, setDeleteLoading] = useState(false);
  // Remote config value
  const isShowClassAllTime = getValue(remoteConfig, 'isShowClassAllTime');
  // User from redux
  const currentUser = useSelector((state) => state.auth.currentUser);
  // Button Loading State
  const [loadingButton, setLoadingButton] = useState(false);
  // Notification
  const { enqueueSnackbar } = useSnackbar();

  const copyToClipboard = async (coupon) => {
    setToolTip(true);

    setTimeout(() => {
      setToolTip(false);
    }, 2000);
    if ('clipboard' in navigator) {
      return navigator.clipboard.writeText(coupon);
    }
    coupenId.current.select();
    document.execCommand('copy');
    return null;
  };

  const updateNotice = async () => {
    setSendBtnLoading(true);
    const classObj = {
      description: notice
    };
    const updateRes = await updateClass(classDetails.id, classObj);
    if (updateRes) {
      classDetails.description = notice;
      dispatch({
        type: CLASS_OPERATIONS,
        payload: {
          action: 'update',
          classObj: classDetails
        }
      });
      setEditNotice(false);
    }
    setSendBtnLoading(false);
  };

  const handleDeleteClass = async () => {
    setDeleteLoading(true);
    await deleteClassById(classDetails.id);
    dispatch({
      type: CLASS_OPERATIONS,
      payload: {
        action: 'delete',
        classObj: classDetails
      }
    });
    navigate('/dashboard/classes');
  };

  return (
    <div className={classes.containerStyle}>
      <ClassInfoCard classDetails={classDetails} handleClassUpdate={handleClassUpdate} />
      <div style={{ marginTop: 20 }}>
        <Card>
          <CardHeader
            action={
              <Button
                variant="text"
                startIcon={editNotice ? null : <Icon icon={edit2Fill} />}
                style={{ height: 'fit-content' }}
                disabled={sendBtnLoading}
                onClick={() => {
                  setEditNotice(!editNotice);
                  setNotice(classDetails?.description || '');
                }}
              >
                {editNotice ? 'Cancel' : 'Edit'}
              </Button>
            }
            title="Notice Board"
          />
          <CardContent>
            {!editNotice ? (
              <Typography>{notice || '-'}</Typography>
            ) : (
              <>
                <TextField
                  fullWidth
                  value={notice}
                  onChange={(e) => setNotice(e.target.value)}
                  margin="dense"
                  placeholder="Type here..."
                  multiline
                  inputProps={{
                    maxLength: 100
                  }}
                  minRows={5}
                  maxRows={5}
                />
                <div className={classes.characterContainer}>{`Character ${notice.length}/100`}</div>
                <div style={{ display: 'flex', marginTop: 15, justifyContent: 'flex-end' }}>
                  <Button
                    variant="text"
                    disabled={sendBtnLoading}
                    onClick={() => setNotice('')}
                    style={{ height: 'fit-content' }}
                  >
                    Clear
                  </Button>
                  <LoadingButton
                    loading={sendBtnLoading}
                    variant="contained"
                    onClick={() => updateNotice()}
                    style={{ height: 'fit-content', marginLeft: 10 }}
                  >
                    Send
                  </LoadingButton>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Card sx={{ marginTop: 3 }}>
          <CardContent>
            <Grid container justifyContent="center" sx={{ flexGrow: 1, alignItems: 'center' }}>
              <Grid item xs={12} sm={6} md={6}>
                <p style={{ color: palette.grey[500] }}>Classroom ID</p>
                <div className="idWithIcon">
                  <h2 ref={coupenId}>{classDetails.sID}</h2>
                  <div>
                    <Tooltip
                      arrow
                      open={toolTip}
                      title="Copied!"
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      placement="top"
                    >
                      <IconButton
                        style={{ marginLeft: 10 }}
                        onClick={() => copyToClipboard('12348268791')}
                      >
                        <CopyToClipBoardIcon color={palette.grey[600]} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                sx={{ textAlign: { xs: 'center', sm: 'end' }, mt: { xs: 1, sm: 0 } }}
              >
                <Button variant="text" color="error" onClick={() => setDeleteModal(true)}>
                  Delete Class
                </Button>
                {(CheckClassIsOnline(classDetails.schedule) !== null ||
                  isShowClassAllTime.asBoolean()) && (
                  <LoadingButton
                    variant="contained"
                    sx={{ ml: 1 }}
                    loading={loadingButton}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLoadingButton(true);
                      capturePHEvents(PH_JOIN_CLASS, {
                        classDetails,
                        userData: currentUser
                      });
                      const attendanceObj = {
                        orgId: classDetails?.orgId?._id ?? classDetails?.orgId,
                        type: 'online',
                        slotId: CheckClassIsOnline(classDetails.schedule)._id,
                        slot: `slot${
                          classDetails.schedule.findIndex(
                            (ts) => ts._id === CheckClassIsOnline(classDetails.schedule)._id
                          ) + 1
                        }`,
                        slotTimeFrom: CheckClassIsOnline(classDetails.schedule).from,
                        slotTimeTo: CheckClassIsOnline(classDetails.schedule).to,
                        presentStudents: [currentUser?.id],
                        absentStudents: []
                      };
                      const res = await createAttendance(classDetails.id, attendanceObj);
                      if (res) {
                        const obj = {
                          atk: getCke('accessToken'),
                          userName: currentUser?.fullName,
                          userRole: currentUser?.profile,
                          userId: currentUser?.id
                        };
                        const encodedObj = Buffer.from(JSON.stringify(obj)).toString('base64');
                        setLoadingButton(false);
                        window.open(
                          `${process.env.REACT_APP_ZOOM_REDIRECT_URL}/joinClass/${classDetails.id}?fud=${encodedObj}`
                        );
                      } else {
                        enqueueSnackbar('Something went wrong, Please try again', {
                          variant: 'error'
                        });
                        setLoadingButton(false);
                      }
                    }}
                  >
                    Join
                  </LoadingButton>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
      <Dialog open={openDeleteModal} onClose={() => setDeleteModal(false)} fullWidth>
        <DialogTitle>Delete Class</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure want to delete{' '}
            <span style={{ fontWeight: 'bold' }}>{classDetails.name}?</span>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={isDeleteLoading} onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <LoadingButton
            loading={isDeleteLoading}
            color="error"
            variant="contained"
            onClick={() => handleDeleteClass()}
          >
            Yes
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

TeacherOverview.propTypes = {
  handleClassUpdate: PropTypes.func,
  classDetails: PropTypes.shape({
    name: PropTypes.string,
    sID: PropTypes.string,
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    id: PropTypes.string,
    category: PropTypes.string,
    subject: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    schedule: PropTypes.arrayOf(
      PropTypes.shape({
        day: PropTypes.string,
        selected: PropTypes.bool,
        slots: PropTypes.arrayOf(
          PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
          })
        )
      })
    )
  })
};
